import { FC, useState, useMemo } from "react";
import { Font,  Page, Text, View, Document, StyleSheet, pdf, Image } from "@react-pdf/renderer";
import { useTranslation } from "react-i18next";
import { Button, App } from "antd";
import { PrinterOutlined } from '@ant-design/icons';
import { getFileUrl } from "@/apis/file";
import zioncom from 'images/system/zioncom.png';
import { FieldSplitor, ProductInfoAttachment, ProductInfoData } from "@/types/productInfo";
import dayjs from "dayjs";
import convertTo2DArray from "@/utils/convert2double";
import fontSrc from '@/assets/fonts/msyh.ttf';
import vnSrc from '@/assets/fonts/NotoSans-Regular.ttf';
import krSrc from '@/assets/fonts/NotoSansKR-Regular.otf';

// 注册支持中文的字体（重复注册幂等；若启动预热失败，首次渲染时在此兜底加载）
Font.register({
  family: "NotoSansSC",
  src: fontSrc
});

Font.register({
  family: "VnFontSrc",
  src: vnSrc
});

Font.register({
  family: "KoFontSrc",
  src: krSrc
});

interface RadioOptions {
  value: number;
  label: string;
}

// 定义 PDF 文档样式
const styles = StyleSheet.create({
  // 页面样式
  page: {
    position: "relative",
    fontFamily: ["VnFontSrc", "KoFontSrc", "NotoSansSC"],
    padding: 20,
    fontSize: 10
  },

  // 头部样式
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
  },
  headerLogo: {
    width: 120,
    height: 50,
    marginTop: -10,
  },
  headerContent: {
    flex: 1,
    marginLeft: -70
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center' as any,
  },
  headerAddress: {
    fontSize: 8,
    textAlign: 'center' as any,
    marginTop: 4,
  },

  // 标题部分
  titleSection: {
    marginBottom: 2,
    textAlign: 'center' as any
  },
  title: {
    fontSize: 6,
  },

  // 内容区域（预留）
  contentArea: {
    flex: 1,
    flexDirection: "column",
    border: '1 solid #000',
    minHeight: 500,
  },

  // 附件页面内容区域 - 固定高度确保2x2布局正确
  attachmentContentArea: {
    border: '1 solid #000',
    height: 700,
  },
  contentPlaceholder: {
    fontSize: 12,
    color: '#999',
    textAlign: 'center' as any,
  },

  // 表格行样式 - 固定下边框
  tableRow: {
    width: "100%",
    flexDirection: 'row',
    borderBottom: '1 solid #000',
  },

  // 表格单元格样式 - 固定左右边框
  tableCell: {
    borderLeft: '1 solid #000',
    borderRight: '1 solid #000',
    padding: 4,
    textAlign: 'center',
  },

  rowFlexCell: {
    flexDirection: "row",
    justifyContent: "flex-start",
    flexWrap: "wrap",
    alignItems: "center",
    borderLeft: '1 solid #000',
    borderRight: '1 solid #000',
  },

  radioBox: {
    marginHorizontal: 4,
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
  },
  radio: {
    width: 10,
    height: 10,
    marginRight: 2
  },
  radioUnActive: {
    borderLeft: '1 solid #000',
    borderRight: '1 solid #000',
    borderTop: '1 solid #000',
    borderBottom: '1 solid #000',
  },
  radioActive: {
    backgroundColor: "#000"
  },
  radioLine: {
    width: 30,
    height: 10,
    borderBottomColor: "#000",
    borderBottomWidth: 1
  },
  // 无边框单元格
  noBorderCell: {
    borderLeft: 'none',
    borderRight: 'none',
    padding: 4,
    textAlign: 'center',
  },

  // 无左边框
  noLeftBorder: {
    borderLeft: 'none',
  },

  // 无右边框
  noRightBorder: {
    borderRight: 'none',
  },

  // 单元格文字
  cellText: {
    fontSize: 9,
    textAlign: 'center',
    width: '100%',
  },
  // 单元格文字
  littleCellText: {
    fontSize: 6,
    textAlign: 'center',
    width: '100%',
  },

  basicTitle: {
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    fontSize: 14,
    gap: 4
  },

  bottomInfo: {
    height: 36,
    paddingTop: 12,
    width: "100%",
    flexDirection: "row",
    marginBottom: 12,
    paddingHorizontal: 24
  },
  bottomItem: {
    flex: 1,
    textAlign: "left"
  },
  bottomText: {
    fontSize: 8
  },

  attemptWrap: {
    width: "100%",
    height: "100%",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    flexWrap: "wrap",
  },

  attemptBox: {
    width: "50%",
    height: "50%",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",
    padding: 4
  },
  attemptTitle: {
    width: "100%",
    textAlign: "left",
    fontSize: 10,
  },
  attemptImageBox: {
    width: "100%",
    flex: 1,
    overflow: "hidden"
  },
  attachmentImage: {
    width: '100%',
    height: "100%"
  },

  processBox: {
    flex: 1,
    width: "100%",
    flexDirection: "column",
  },
  processCell: {
    maxHeight: 60,
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  processRow: {
    flex: 1,
    borderBottomWidth: 0
  },

  // 页脚
  footer: {
    position: "absolute",
    bottom: 20,
    left: 0,
    right: 0,
    textAlign: 'center' as any,
    fontSize: 8,
  }
});

interface PrintProductInfoProps {
  data: ProductInfoData;
  disabled?: boolean;
}

// 头部组件
const Header = ({ picUrl, name, address }: { picUrl: string; name: string; address: string }) => (
  <View style={styles.header}>
    <Image src={picUrl} style={styles.headerLogo} />
    <View style={styles.headerContent}>
      <Text style={styles.headerTitle}>{name}</Text>
      <Text style={styles.headerAddress}>{address}</Text>
    </View>
  </View>
);

const PrintProductInfo: FC<PrintProductInfoProps> = ({ data, disabled = false }) => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const [loading, setLoading] = useState<boolean>(false);

  // 获取公司logo
  const { picUrl, name, address } = {
    picUrl: zioncom,
    name: "佶翁（深圳）电子科技有限公司",
    address: "Zioncom(Shenzhen) Electronic Technology Co.,Ltd"
  };

  // 获取附件图片URL
  const getAttachmentImageUrl = (docId: string) => {
    return getFileUrl(docId);
  };
  // 语言选项
  const languageOptions = [
    { label: t("product.languageKo"), value: 1 },
    { label: t("product.languageZh"), value: 2 },
    { label: t("product.languageEn"), value: 3 },
    { label: t("product.languageFr"), value: 4 },
    { label: t("product.languageDe"), value: 5 },
    { label: t("product.languageRu"), value: 6 },
    { label: t("product.languageAdaptive"), value: 7 },
    { label: t("product.languagePortuguese"), value: 8 },
  ];
  // channel选项
  const channelOptions = [
    { label: "1~11", value: 1 },
    { label: "1~13", value: 2 },
    { label: "1~14", value: 3 },
  ];
  //  信道选择
  const channelSelections = [
    { label: "FCC", value: 1 },
    { label: "ETSI", value: 2 },
    { label: "MKK", value: 3 },
  ];
  //  默认信道
  const defaultChannels = [
    { label: "6", value: 1 },
    { label: t("product.auto"), value: 2 },
    { label: "13", value: 3 },
    { label: t("supplierLayout.other"), value: 4 },
  ];

  // PDF文档
  const MyDocument = useMemo(() => {
    let attachments2D: Array<Array<ProductInfoAttachment>> = [];
    if (data.attachments && data.attachments.length > 0) {
      attachments2D = convertTo2DArray(data.attachments, 4)
    }
    return (
      <Document>
        {/* 第一页：生产资料内容（预留） */}
        <Page size="A4" orientation="portrait" style={styles.page}>
          {/* 头部 */}
          <Header picUrl={picUrl} name={name} address={address} />

          {/* 标题 */}
          <View style={styles.titleSection}>
            <Text style={styles.title}>生产资料说明书</Text>
          </View>

          {/* 内容区域 - 预留，稍后完善字段渲染 */}
          <View style={styles.contentArea}>
            {/* 第一行：6个单元格，标签+值交替排列 */}
            <View style={styles.tableRow}>
              {/* 订单号 - 第一个格子，无左右边框 */}
              <View style={[styles.noBorderCell, { width: "10%" }]}>
                <Text style={styles.cellText}>订单号</Text>
              </View>
              {/* 订单号 值 */}
              <View style={[styles.tableCell, { width: "25%" }]}>
                <Text style={styles.cellText}>{data.saleOrderItem?.order?.number || ''}</Text>
              </View>
              {/* ERP料号 标签 */}
              <View style={[styles.tableCell, styles.noLeftBorder, { width: "15%" }]}>
                <Text style={styles.cellText}>ERP料号</Text>
              </View>
              {/* ERP料号 值 */}
              <View style={[styles.tableCell, styles.noLeftBorder, { width: "18%" }]}>
                <Text style={styles.cellText}>{data.product?.code || ''}</Text>
              </View>
              {/* 发行日期 标签 */}
              <View style={[styles.tableCell, styles.noLeftBorder, { width: "17%" }]}>
                <Text style={styles.cellText}>发行日期</Text>
              </View>
              {/* 发行日期 - 最后一个格子，无左右边框 */}
              <View style={[styles.noBorderCell, { width: "15%" }]}>
                <Text style={styles.cellText}>{data.issueDate ? dayjs(data.issueDate).format('YYYY-MM-DD') : ''}</Text>
              </View>
            </View>
            {/* 第二行：6个单元格，标签+值交替排列 */}
            <View style={styles.tableRow}>
              <View style={[styles.noBorderCell, { width: "10%" }]}>
                <Text style={styles.cellText}>客户</Text>
              </View>
              <View style={[styles.tableCell, { width: "25%" }]}>
                <Text style={styles.cellText}>{data.customerName || ''}</Text>
              </View>
              <View style={[styles.tableCell, styles.noLeftBorder, { width: "15%" }]}>
                <Text style={styles.cellText}>客户型号</Text>
              </View>
              <View style={[styles.tableCell, styles.noLeftBorder, { width: "18%" }]}>
                <Text style={styles.cellText}>{data.customerModel || ''}</Text>
              </View>
              <View style={[styles.tableCell, styles.noLeftBorder, { width: "17%" }]}>
                <Text style={styles.cellText}>公司型号</Text>
              </View>
              <View style={[styles.noBorderCell, { width: "15%" }]}>
                <Text style={styles.cellText}>{data.companyModel || ''}</Text>
              </View>
            </View>

            {/* 第三行：左右两个部分 */}
            <View style={styles.tableRow}>
              <View style={[styles.tableCell, styles.noLeftBorder, styles.basicTitle, { width: "10%" }]}>
                <Text>基</Text>
                <Text>本</Text>
                <Text>资</Text>
                <Text>料</Text>
                <Text>说</Text>
                <Text>明</Text>
              </View>
              <View style={[styles.noBorderCell, { width: "90%", padding: 0 }]}>
                <View style={styles.tableRow}>
                  <View style={[styles.noBorderCell, { width: "15%" }]}>
                    <Text style={styles.cellText}>生产软件</Text>
                  </View>
                  <View style={[styles.tableCell, { width: "45%" }]}>
                    <Text style={styles.cellText}>{data.productionSoftware || ''}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.noLeftBorder, { width: "15%" }]}>
                    <Text style={styles.cellText}>Flash</Text>
                  </View>
                  <View style={[styles.noBorderCell, { width: "25%" }]}>
                    <Text style={styles.cellText}>{data.flash || ''}</Text>
                  </View>
                </View>

                <View style={styles.tableRow}>
                  <View style={[styles.noBorderCell, { width: "15%" }]}>
                    <Text style={styles.cellText}>MD5值</Text>
                  </View>
                  <View style={[styles.tableCell, { width: "45%" }]}>
                    <Text style={styles.cellText}>{data.md5Value || ''}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.noLeftBorder, { width: "15%" }]}>
                    <Text style={styles.cellText}>Checksum</Text>
                  </View>
                  <View style={[styles.noBorderCell, { width: "25%" }]}>
                    <Text style={styles.cellText}>{data.checksum || ''}</Text>
                  </View>
                </View>

                <View style={styles.tableRow}>
                  <View style={[styles.noBorderCell, { width: "15%" }]}>
                    <Text style={styles.cellText}>QA工具版本</Text>
                  </View>
                  <View style={[styles.tableCell, { width: "45%" }]}>
                    <Text style={styles.cellText}>{data.qaToolVersion || ''}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.noLeftBorder, { width: "15%" }]}>
                    <Text style={styles.cellText}>软件版本</Text>
                  </View>
                  <View style={[styles.noBorderCell, { width: "25%" }]}>
                    <Text style={styles.cellText}>{data.softwareVersion || ''}</Text>
                  </View>
                </View>

                <View style={styles.tableRow}>
                  <View style={[styles.noBorderCell, { width: "15%" }]}>
                    <Text style={styles.cellText}>MAC地址内容</Text>
                  </View>
                  <View style={[styles.tableCell, styles.noRightBorder, { width: "85%", padding: 0 }]}>
                    <View style={styles.tableRow}>
                      <View style={[styles.noBorderCell, { width: "100%" }]}>
                        <Text style={styles.cellText}>{data?.macAddressContent ?? ''}</Text>
                      </View>
                    </View>
                    <View style={{ ...styles.tableRow, borderBottomWidth: 0 }}>
                      <View style={
                        [
                          styles.tableCell,
                          styles.noBorderCell,
                          { width: "25%" }
                        ]
                      }>
                        <Text style={styles.cellText}>数量</Text>
                      </View>
                      <View style={
                        [
                          styles.tableCell,
                          styles.noRightBorder,
                          { width: "25%" }
                        ]
                      }>
                        <Text style={styles.cellText}>{data?.quantity ?? ''}</Text>
                      </View>
                      <View style={
                        [
                          styles.tableCell,
                          styles.noRightBorder,
                          { width: "25%" }
                        ]
                      }>
                        <Text style={styles.cellText}>尺寸</Text>
                      </View>
                      <View style={
                        [
                          styles.tableCell,
                          styles.noRightBorder,
                          { width: "25%" }
                        ]
                      }>
                        <Text style={styles.cellText}>{data?.size ?? ''}</Text>
                      </View>
                    </View>
                  </View>
                </View>

                <View style={styles.tableRow}>
                  <View style={[styles.noBorderCell, { width: "15%" }]}>
                    <Text style={styles.cellText}>软件杀毒</Text>
                  </View>
                  <View style={[styles.tableCell, styles.noRightBorder, { width: "85%", padding: 0 }]}>
                    <Text style={styles.cellText}>{data.antivirusStatus || ''}</Text>
                  </View>
                </View>
                {/* 语言单选行 */}
                <View style={styles.tableRow}>
                  <View style={[styles.noBorderCell, { width: "15%" }]}>
                    <Text style={styles.cellText}>语言要求</Text>
                  </View>
                  <View style={[styles.rowFlexCell, styles.noRightBorder, { width: "85%", padding: 4 }]}>
                    {
                      languageOptions.map(opt => (
                        <View style={styles.radioBox}>
                          <View style={[styles.radio, data.languageRequirement === opt.value ? styles.radioActive : styles.radioUnActive]} />
                          <Text>{opt.label}</Text>
                          {opt.value === 8 && <View style={styles.radioLine} />}
                        </View>
                      ))
                    }
                  </View>
                </View>

                <View style={styles.tableRow}>
                  <View style={[styles.noBorderCell, { width: "15%" }]}>
                    <Text style={styles.cellText}>SSID</Text>
                  </View>
                  <View style={[styles.tableCell, { width: "45%" }]}>
                    <Text style={styles.cellText}>{data.ssid || ''}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.noLeftBorder, { width: "15%" }]}>
                    <Text style={styles.cellText}>初始IP</Text>
                  </View>
                  <View style={[styles.noBorderCell, { width: "25%" }]}>
                    <Text style={styles.cellText}>{data.initialIp || ''}</Text>
                  </View>
                </View>


                <View style={styles.tableRow}>
                  <View style={[styles.noBorderCell, { width: "15%" }]}>
                    <Text style={styles.cellText}>产测程序</Text>
                  </View>
                  <View style={[styles.tableCell, styles.noRightBorder, { width: "15%", fontSize: 8 }]}>
                    <Text style={styles.cellText}>成品测试 工具版本</Text>
                  </View>
                  <View style={[styles.tableCell, { width: "30%" }]}>
                    <Text style={styles.cellText}>{data.finishedToolVersion || ''}</Text>
                  </View>
                  <View style={[styles.tableCell, styles.noLeftBorder, { width: "15%", fontSize: 8 }]}>
                    <Text style={styles.cellText}>半成品测试 工具版本</Text>
                  </View>
                  <View style={[styles.noBorderCell, { width: "25%" }]}>
                    <Text style={styles.cellText}>{data.semiFinishedToolVersion || ''}</Text>
                  </View>
                </View>

                <View style={styles.tableRow}>
                  <View style={[styles.noBorderCell, { width: "15%" }]}>
                    <Text style={styles.cellText}>channel</Text>
                  </View>
                  <View style={[styles.rowFlexCell, styles.noRightBorder, { width: "85%", justifyContent: "space-evenly", padding: 4 }]}>
                    {
                      channelOptions.map(opt => (
                        <View style={styles.radioBox}>
                          <View style={[styles.radio, data.channelRange === opt.value ? styles.radioActive : styles.radioUnActive]} />
                          <Text>{opt.label}</Text>
                        </View>
                      ))
                    }
                  </View>
                </View>

                <View style={styles.tableRow}>
                  <View style={[styles.noBorderCell, { width: "15%" }]}>
                    <Text style={styles.cellText}>信道选择</Text>
                  </View>
                  <View style={[styles.rowFlexCell, styles.noRightBorder, { width: "85%", justifyContent: "space-evenly", padding: 4 }]}>
                    {
                      channelSelections.map(opt => (
                        <View style={styles.radioBox}>
                          <View style={[styles.radio, data.channelSelection === opt.value ? styles.radioActive : styles.radioUnActive]} />
                          <Text>{opt.label}</Text>
                        </View>
                      ))
                    }
                  </View>
                </View>

                <View style={styles.tableRow}>
                  <View style={[styles.noBorderCell, { width: "15%" }]}>
                    <Text style={styles.cellText}>默认信道</Text>
                  </View>
                  <View style={[styles.rowFlexCell, styles.noRightBorder, { width: "85%", justifyContent: "space-evenly", padding: 4 }]}>
                    {
                      defaultChannels.map(opt => (
                        <View style={styles.radioBox}>
                          <View style={[styles.radio, data.defaultChannel === opt.value ? styles.radioActive : styles.radioUnActive]} />
                          <Text>{opt.label}</Text>
                          {opt.value === 4 && <View style={styles.radioLine} />}
                          {opt.value === 4 && <Text>5G:36（{t("product.region36to165")}）</Text>}
                        </View>
                      ))
                    }
                  </View>
                </View>

                <View style={styles.tableRow}>
                  <View style={[styles.noBorderCell, { width: "15%" }]}>
                    <Text style={styles.cellText}>功能简介</Text>
                  </View>
                  <View style={[styles.rowFlexCell, styles.noRightBorder, { width: "85%" }]}>
                    {
                      data.functionDescription?.split(FieldSplitor)?.map((str, index) => (
                        <View style={
                          [
                            styles.tableCell,
                            [0, 4].includes(index) ? styles.noBorderCell : styles.noRightBorder,
                            {
                              width: "25%",
                              borderBottomWidth: index < 4 ? 1 : 0,
                            }
                          ]
                        }>
                          <Text style={styles.cellText}>{str}</Text>
                        </View>
                      ))
                    }
                  </View>
                </View>

                <View style={styles.tableRow}>
                  <View style={[styles.noBorderCell, { width: "15%" }]}>
                    <Text style={styles.cellText}>产测程序</Text>
                  </View>
                  <View style={[styles.rowFlexCell, styles.noRightBorder, { width: "85%" }]}>
                    {
                      data.productionTestProgram?.split(FieldSplitor)?.map((str, index) => (
                        <View style={
                          [
                            styles.tableCell,
                            [0, 4].includes(index) ? styles.noBorderCell : styles.noRightBorder,
                            {
                              width: "25%",
                            }
                          ]
                        }>
                          <Text style={styles.cellText}>{str}</Text>
                        </View>
                      ))
                    }
                  </View>
                </View>

                <View style={styles.tableRow}>
                  <View style={[styles.noBorderCell, { width: "15%" }]}>
                    <Text style={styles.cellText}>POWER(dbm)</Text>
                  </View>
                  <View style={[styles.rowFlexCell, styles.noRightBorder, { width: "85%" }]}>
                    {
                      data.power?.split(FieldSplitor)?.map((str, index) => (
                        <View style={
                          [
                            styles.tableCell,
                            [0, 4].includes(index) ? styles.noBorderCell : styles.noRightBorder,
                            {
                              width: "25%",
                            }
                          ]
                        }>
                          <Text style={styles.cellText}>{str}</Text>
                        </View>
                      ))
                    }
                  </View>
                </View>

                <View style={styles.tableRow}>
                  <View style={[styles.noBorderCell, { width: "15%" }]}>
                    <Text style={styles.cellText}>EVM(db)</Text>
                  </View>
                  <View style={[styles.rowFlexCell, styles.noRightBorder, { width: "85%" }]}>
                    {
                      data.evm?.split(FieldSplitor)?.map((str, index) => (
                        <View style={
                          [
                            styles.tableCell,
                            [0, 4].includes(index) ? styles.noBorderCell : styles.noRightBorder,
                            {
                              width: "25%",
                            }
                          ]
                        }>
                          <Text style={styles.cellText}>{str}</Text>
                        </View>
                      ))
                    }
                  </View>
                </View>

                <View style={{ ...styles.tableRow, borderBottomWidth: 0 }}>
                  <View style={[styles.noBorderCell, { width: "15%" }]}>
                    <Text style={styles.cellText}>FER(dbm)</Text>
                  </View>
                  <View style={[styles.rowFlexCell, styles.noRightBorder, { width: "85%" }]}>
                    {
                      data.fer?.split(FieldSplitor)?.map((str, index) => (
                        <View style={
                          [
                            styles.tableCell,
                            [0, 4].includes(index) ? styles.noBorderCell : styles.noRightBorder,
                            {
                              width: "25%",
                            }
                          ]
                        }>
                          <Text style={styles.cellText}>{str}</Text>
                        </View>
                      ))
                    }
                  </View>
                </View>

              </View>
            </View>

            {/* 7行内容带修改 */}
            <View style={styles.processBox}>
              <View style={[styles.tableRow, styles.processCell]}>
                <View style={[styles.noBorderCell, { width: "10%" }]}>
                  <Text style={styles.littleCellText}>SMT</Text>
                </View>
                <View style={[styles.tableCell, styles.noRightBorder, { width: "85%", padding: 0, paddingLeft: 2 }]}>
                  <Text style={{ ...styles.littleCellText, textAlign: "left" }}>{data.smt || ''}</Text>
                </View>
              </View>

              <View style={[styles.tableRow, styles.processCell]}>
                <View style={[styles.noBorderCell, { width: "10%" }]}>
                  <Text style={styles.littleCellText}>DIP</Text>
                </View>
                <View style={[styles.tableCell, styles.noRightBorder, { width: "85%", padding: 0, paddingLeft: 2 }]}>
                  <Text style={{ ...styles.littleCellText, textAlign: "left" }}>{data.dip || ''}</Text>
                </View>
              </View>

              <View style={[styles.tableRow, styles.processCell]}>
                <View style={[styles.noBorderCell, { width: "10%" }]}>
                  <Text style={styles.littleCellText}>TEST1</Text>
                </View>
                <View style={[styles.tableCell, styles.noRightBorder, { width: "85%", padding: 0, paddingLeft: 2 }]}>
                  <Text style={{ ...styles.littleCellText, textAlign: "left" }}>{data.test1 || ''}</Text>
                </View>
              </View>

              <View style={[styles.tableRow, styles.processCell]}>
                <View style={[styles.noBorderCell, { width: "10%" }]}>
                  <Text style={styles.littleCellText}>ASSY</Text>
                </View>
                <View style={[styles.tableCell, styles.noRightBorder, { width: "85%", padding: 0, paddingLeft: 2 }]}>
                  <Text style={{ ...styles.littleCellText, textAlign: "left" }}>{data.assy || ''}</Text>
                </View>
              </View>

              <View style={[styles.tableRow, styles.processCell]}>
                <View style={[styles.noBorderCell, { width: "10%" }]}>
                  <Text style={styles.littleCellText}>TEST2</Text>
                </View>
                <View style={[styles.tableCell, styles.noRightBorder, { width: "85%", padding: 0, paddingLeft: 2 }]}>
                  <Text style={{ ...styles.littleCellText, textAlign: "left" }}>{data.test2 || ''}</Text>
                </View>
              </View>

              <View style={[styles.tableRow, styles.processCell]}>
                <View style={[styles.noBorderCell, { width: "10%" }]}>
                  <Text style={styles.littleCellText}>PACK</Text>
                </View>
                <View style={[styles.tableCell, styles.noRightBorder, { width: "85%", padding: 0, paddingLeft: 2 }]}>
                  <Text style={{ ...styles.littleCellText, textAlign: "left" }}>{data.pack || ''}</Text>
                </View>
              </View>

              <View style={[styles.tableRow, styles.processRow]}>
                <View style={[styles.noBorderCell, { width: "10%" }]}>
                  <Text style={styles.littleCellText}>出货要求</Text>
                </View>
                <View style={[styles.tableCell, styles.noRightBorder, { width: "85%", padding: 0, paddingLeft: 2 }]}>
                  <Text style={{ ...styles.littleCellText, textAlign: "left" }}>{data.shipmentRequirements || ''}</Text>
                </View>
              </View>
            </View>

          </View>

          <View style={styles.bottomInfo}>
            <View style={styles.bottomItem}>
              <Text style={styles.bottomText}>核准：</Text>
            </View>
            <View style={styles.bottomItem}>
              <Text style={styles.bottomText}>审核：</Text>
            </View>
            <View style={styles.bottomItem}>
              <Text style={styles.bottomText}>确认：</Text>
            </View>
            <View style={styles.bottomItem}>
              <Text style={styles.bottomText}>制作：</Text>
            </View>
          </View>

          {/* 页脚 */}
          <Text style={styles.footer} render={({ pageNumber, totalPages }) =>
            `第 ${pageNumber} 页 / 共 ${totalPages} 页`
          } fixed />
        </Page>

        {/* 附件页面：从第二页开始，每页一个附件 */}
        {attachments2D.map((attachments, index) => (
          <Page size="A4" orientation="portrait" style={styles.page} key={`attachment-${index}`}>
            {/* 头部 */}
            <Header picUrl={picUrl} name={name} address={address} />

            {/* 标题 */}
            <View style={styles.titleSection}>
              <Text style={styles.title}>生产资料说明书</Text>
            </View>
            <View style={styles.attachmentContentArea}>
              <View style={styles.attemptWrap}>
                {
                  attachments.map(attachment => (
                    <View style={styles.attemptBox}>
                      <Text style={styles.attemptTitle}>{attachment?.description ?? ''}</Text>
                      <View style={styles.attemptImageBox}>
                        <Image
                          src={getAttachmentImageUrl(attachment.docId)}
                          style={styles.attachmentImage}
                        />
                      </View>
                    </View>
                  ))
                }
              </View>
            </View>

            <View style={styles.bottomInfo}>
              <View style={styles.bottomItem}>
                <Text style={styles.bottomText}>核准：</Text>
              </View>
              <View style={styles.bottomItem}>
                <Text style={styles.bottomText}>审核：</Text>
              </View>
              <View style={styles.bottomItem}>
                <Text style={styles.bottomText}>确认：</Text>
              </View>
              <View style={styles.bottomItem}>
                <Text style={styles.bottomText}>制作：</Text>
              </View>
            </View>

            {/* 页脚 */}
            <Text style={styles.footer} render={({ pageNumber, totalPages }) =>
              `第 ${pageNumber} 页 / 共 ${totalPages} 页`
            } fixed />
          </Page>
        ))}

      </Document>
    );
  }, [data, picUrl, name, address, languageOptions, channelOptions, defaultChannels]);

  // 处理PDF打印
  const handlePDF = async () => {
    setLoading(true);

    try {
      const blob = await pdf(MyDocument).toBlob();
      const pdfUrl = URL.createObjectURL(blob);

      // 创建隐藏的iframe用于打印
      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = pdfUrl;

      // 将iframe添加到body中
      document.body.appendChild(iframe);

      iframe.onload = () => {
        console.log('PDF 加载完成');
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (error) {
          console.error('打印失败:', error);
          notification.error({ placement: 'top', message: '打印失败' });
        } finally {
          setLoading(false);
        }
      };

    } catch (error) {
      console.error('PDF生成失败:', error);
      notification.error({
        placement: 'top',
        message: 'PDF生成失败',
        description: '请稍后重试'
      });
      setLoading(false);
    }
  };

  return (
    <Button
      type="primary"
      icon={<PrinterOutlined />}
      onClick={handlePDF}
      loading={loading}
      disabled={disabled}
    >
      {t('common.printInfo')}
    </Button>
  );
};

export default PrintProductInfo;